import { effectByNumber } from "../rules/loader.ts";
import type { Rules } from "../rules/types.ts";

/**
 * The speed a character moves at (ADR-144's amendment of 2026-11-01, decision 2): the rules' walk or run speed scaled by
 * Agility's movement factor and the effects it bears, one pure function for the map server, which decides, and the page,
 * which predicts its own steps from the effects its owner frame confirmed. Integer maths, no allocation.
 */

/**
 * The most the effects together may slow a character, in whole percents.
 */
export const EFFECT_SPEED_FLOOR = -50;

/**
 * The speed a step is taken at.
 *
 * @param base The rules' `walk_speed` or `run_speed`, in speed units.
 * @param agilityBp Agility's movement factor in basis points, 0 for none (ADR-138; P2.22-T29 feeds it).
 * @param effectPercent The sum of the effects' `moveSpeed`, in whole percents, held at `EFFECT_SPEED_FLOOR`.
 * @returns The speed in speed units, rounded to the nearest (a half up).
 * @example
 * effectiveSpeed(160, 0, -5);
 * // 152: a Drenched run
 */
export function effectiveSpeed(base: number, agilityBp: number, effectPercent: number): number {
  const effects = Math.max(EFFECT_SPEED_FLOOR, effectPercent);
  return Math.floor((base * (10_000 + agilityBp + 100 * effects) + 5_000) / 10_000);
}

/**
 * The sum of the `moveSpeed` of the effects an owner frame lists, each counted once per stack; a number the rules do not
 * know adds nothing.
 *
 * @param rules The rules.
 * @param effects The frame's entries.
 * @param count How many of them are in force.
 * @returns The percent, 0 for none.
 */
export function moveSpeedOfEffects(rules: Rules, effects: readonly { readonly effect: number; readonly stacks: number }[], count: number): number {
  let percent = 0;
  for (let index = 0; index < count; index += 1) {
    const entry = effects[index];
    const effect = entry === undefined ? undefined : effectByNumber(rules, entry.effect);
    if (entry !== undefined && effect !== undefined) {
      percent += (effect.modifiers.moveSpeed ?? 0) * entry.stacks;
    }
  }
  return percent;
}
